import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Input, Image, Upload, Button, notification } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { FileUploadIcon } from 'components/Elements/CustomSVGIcon';
import AuthImage from 'components/Elements/AuthImage';
import { AppDispatch } from 'core/types';
import { SiteImage } from 'core/sites/types';
import { stripHtmlTags } from 'core/utils/helpers';
import { uploadSiteImage, updateSiteImage, deleteSiteImage } from 'core/sites/sitesApi';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';
import './siteImages.scss';

interface SiteImagesProps {
    encryptedId: string;
    siteImages: SiteImage[];
}

const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/gif,image/webp';
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const getFileExtension = (filename: string): string => {
    const parts = filename.split('.');
    if (parts.length <= 1) return '';
    const ext = parts[parts.length - 1];
    return ext ? ext.toLowerCase() : '';
};

const { Dragger } = Upload;

const SiteImages: FC<SiteImagesProps> = ({ encryptedId, siteImages }) => {
    const dispatch = useDispatch<AppDispatch>();

    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadDescription, setUploadDescription] = useState('');
    const [editingImage, setEditingImage] = useState<SiteImage | null>(null);
    const [editDescription, setEditDescription] = useState('');
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deletingImage, setDeletingImage] = useState<SiteImage | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleUpload = async () => {
        if (!selectedFile) return;
        setIsUploading(true);
        try {
            const result = await dispatch(uploadSiteImage({
                encryptedId,
                file: selectedFile,
                ...(uploadDescription ? { description: uploadDescription } : {}),
            }));
            if (uploadSiteImage.fulfilled.match(result)) {
                setUploadModalVisible(false);
                setSelectedFile(null);
                setUploadDescription('');
            }
        } finally {
            setIsUploading(false);
        }
    };

    const handleEdit = (image: SiteImage) => {
        setEditingImage(image);
        setEditDescription(image.description || '');
        setEditModalVisible(true);
    };

    const handleUpdate = async () => {
        if (!editingImage) return;
        setIsUpdating(true);
        try {
            const result = await dispatch(updateSiteImage({
                encryptedId,
                imageId: editingImage.id,
                description: editDescription || null,
            }));
            if (updateSiteImage.fulfilled.match(result)) {
                setEditModalVisible(false);
                setEditingImage(null);
                setEditDescription('');
            }
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = (image: SiteImage) => {
        setDeletingImage(image);
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (!deletingImage) return;
        setIsDeleting(true);
        try {
            const result = await dispatch(deleteSiteImage({ encryptedId, imageId: deletingImage.id }));
            if (deleteSiteImage.fulfilled.match(result)) {
                setDeleteModalVisible(false);
                setDeletingImage(null);
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const handleBeforeUpload = (file: File) => {
        const ext = getFileExtension(file.name);
        if (!ALLOWED_EXTENSIONS.includes(ext)) {
            notification.error({
                message: 'Unsupported File Type',
                description: `The file "${file.name}" has an unsupported extension. Allowed: ${ALLOWED_EXTENSIONS.join(', ').toUpperCase()}.`,
            });
            return Upload.LIST_IGNORE;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            notification.error({
                message: 'File Too Large',
                description: `The file "${file.name}" exceeds the maximum size of 10MB.`,
            });
            return Upload.LIST_IGNORE;
        }

        setSelectedFile(file);
        return false;
    };

    return (
        <div className="site-images-section">
            <div className="site-images-header">
                <h3 className="site-images-title">Site Images</h3>
                <button
                    type="button"
                    className="upload-btn"
                    onClick={() => setUploadModalVisible(true)}>
                    <PlusOutlined />
                    Upload Image
                </button>
            </div>

            {siteImages && siteImages.length > 0 ? (
                <Image.PreviewGroup>
                    <div className="site-images-grid">
                        {siteImages.map((image) => (
                            <div className="site-image-card" key={image.id}>
                                <div className="image-wrapper">
                                    <AuthImage
                                        src={image.url}
                                        alt={image.description || image.filename}
                                        width="100%"
                                        height="100%"
                                        style={{ objectFit: 'cover' }} />
                                </div>
                                <div className="image-info">
                                    {image.description && (
                                        <p className="image-description">{stripHtmlTags(image.description)}</p>
                                    )}
                                    <p className="image-meta">
                                        {image.uploadedBy && <span>{image.uploadedBy}</span>}
                                        {image.uploadedBy && image.uploadDate && <span> &middot; </span>}
                                        {image.uploadDate && <span>{image.uploadDate}</span>}
                                    </p>
                                    <div className="image-actions">
                                        <button
                                            type="button"
                                            className="image-action-btn"
                                            onClick={() => handleEdit(image)}>
                                            <EditOutlined />
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            className="image-action-btn image-action-btn--delete"
                                            onClick={() => handleDelete(image)}>
                                            <DeleteOutlined />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Image.PreviewGroup>
            ) : (
                <div className="no-images-message">No images uploaded yet.</div>
            )}

            <Modal
                title="Upload Image"
                open={uploadModalVisible}
                centered
                footer={null}
                width={650}
                wrapClassName="site-image-modal"
                onCancel={() => {
                    setUploadModalVisible(false);
                    setSelectedFile(null);
                    setUploadDescription('');
                }}>
                <Dragger
                    accept={ACCEPTED_IMAGE_TYPES}
                    beforeUpload={handleBeforeUpload}
                    onRemove={() => setSelectedFile(null)}
                    showUploadList={false}
                    className="file-dragger-container">
                    <div className="file-uploader-content">
                        <div className="icon-wrapper">
                            <FileUploadIcon />
                        </div>
                        <p className="caption">
                            {selectedFile ? selectedFile.name : 'Drag and drop an image here, or click to upload'}
                        </p>
                        <p className="sub-caption">
                            Supports JPG, PNG, GIF, WebP (max 10MB)
                        </p>
                    </div>
                </Dragger>
                <Input.TextArea
                    placeholder="Description (optional)"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    className="notes-textarea"
                    rows={3}
                    maxLength={MAX_LONG_TEXT_LENGTH} />
                <div className="modal-buttons">
                    <Button
                        type="primary"
                        className="save-button"
                        disabled={!selectedFile}
                        loading={isUploading}
                        onClick={handleUpload}>
                        Upload
                    </Button>
                </div>
            </Modal>

            <Modal
                title="Edit Description"
                open={editModalVisible}
                centered
                footer={null}
                width={650}
                wrapClassName="site-image-modal"
                onCancel={() => {
                    setEditModalVisible(false);
                    setEditingImage(null);
                    setEditDescription('');
                }}>
                <Input.TextArea
                    placeholder="Description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="notes-textarea"
                    rows={3}
                    maxLength={MAX_LONG_TEXT_LENGTH} />
                <div className="modal-buttons">
                    <Button
                        type="primary"
                        className="save-button"
                        loading={isUpdating}
                        onClick={handleUpdate}>
                        Save
                    </Button>
                </div>
            </Modal>

            <Modal
                title="Delete Image"
                open={deleteModalVisible}
                centered
                footer={null}
                width={650}
                wrapClassName="site-image-modal"
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setDeletingImage(null);
                }}>
                <p className="delete-confirm-text">Are you sure you want to delete this image?</p>
                <div className="button-wrapper">
                    <button
                        type="button"
                        className="apply-button"
                        disabled={isDeleting}
                        onClick={handleConfirmDelete}>
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                        type="button"
                        className="cancel-button"
                        onClick={() => {
                            setDeleteModalVisible(false);
                            setDeletingImage(null);
                        }}>
                        Cancel
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default SiteImages;
